/**
 * Feiertage — Verwaltung der Feiertage pro Standort und Jahr.
 *
 * Features:
 * - Jahresübersicht mit Kalenderansicht
 * - CRUD für Feiertage (Datum, Name, Standort-Zuordnung)
 * - Filterung nach Standort
 *
 * @page /feiertage
 * @roles admin, manager
 * @i18n holidays, common
 * @endpoints GET/POST/PUT/DELETE /api/feiertage, GET /api/standorte
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { PageHeader, Modal, ToggleSwitch } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ───────────────────────────────────────────────────────

function api(path, opts = {}) {
  return fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then((r) => r.json());
}

function formatDate(d) {
  return `${d.getDate()}.${d.getMonth()+1}.${d.getFullYear()}`;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
}

// ─── Modal (from Design System) ─────────────────────────────────────

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content placeholder-content-dim focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30"
    />
  );
}

function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-line-hover"
    >
      {children}
    </select>
  );
}

// ─── Month Grid ────────────────────────────────────────────────────

function MonthGrid({ jahr, monat, feiertagSet, onDayClick, t, MONATE, WOCHENTAGE_SHORT }) {
  const firstDay = new Date(jahr, monat, 1);
  const daysInMonth = new Date(jahr, monat + 1, 0).getDate();
  // Monday-based: 0=Mo, 6=So
  let startDay = firstDay.getDay() - 1;
  if (startDay < 0) startDay = 6;

  const cells = [];
  // Leading empty cells
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // Count arbeitstage
  let arbeitstage = 0;
  const today = new Date();
  today.setHours(0,0,0,0);

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(jahr, monat, d);
    const jsDay = date.getDay();
    const isWeekend = jsDay === 0 || jsDay === 6;
    const ds = isoDate(date);
    const isFeiertag = feiertagSet.has(ds);
    if (!isWeekend && !isFeiertag) arbeitstage++;
  }

  return (
    <div className="bg-surface-card border border-line rounded-card p-3 shadow-card hover:border-line-hover transition-colors">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-content">{MONATE[monat]}</h3>
        <span className="text-[10px] font-mono text-[var(--text-secondary)]">{arbeitstage} AT</span>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WOCHENTAGE_SHORT.map((w,i) => (
          <div key={w} className={`text-center text-[9px] font-medium ${i >= 5 ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'}`}>{w}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} className="h-6" />;
          const date = new Date(jahr, monat, d);
          const jsDay = date.getDay();
          const isWeekend = jsDay === 0 || jsDay === 6;
          const ds = isoDate(date);
          const feiertag = feiertagSet.get(ds);
          const isToday = isoDate(today) === ds;

          let bg = '';
          let textColor = 'text-content-secondary';
          if (feiertag) {
            bg = 'bg-red-500/20';
            textColor = 'text-red-400';
          } else if (isWeekend) {
            bg = 'bg-surface-card';
            textColor = 'text-[var(--text-tertiary)]';
          }

          return (
            <div
              key={d}
              onClick={() => feiertag && onDayClick?.(feiertag)}
              className={`h-6 flex items-center justify-center rounded text-[10px] font-mono relative
                ${bg} ${textColor}
                ${isToday ? 'ring-1 ring-amber-500/50' : ''}
                ${feiertag ? 'cursor-pointer hover:bg-red-500/30' : ''}`}
              title={feiertag ? feiertag.name : undefined}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────

export default function Feiertage() {
  const { t } = useLanguage();

  const MONATE = Array.from({length: 12}, (_, i) => t(`common.months.${i}`));
  const MONATE_SHORT = Array.from({length: 12}, (_, i) => t(`common.months_short.${i}`));
  const WOCHENTAGE_SHORT = [t('common.weekdays.1'), t('common.weekdays.2'), t('common.weekdays.3'), t('common.weekdays.4'), t('common.weekdays.5'), t('common.weekdays.6'), t('common.weekdays.0')];
  // For day names indexed by JS getDay() (0=Sun, 1=Mon, ..., 6=Sat)
  const DAY_NAMES = Array.from({length: 7}, (_, i) => t(`common.weekdays.${i}`));

  const [jahr, setJahr] = useState(2026);
  const [feiertage, setFeiertage] = useState([]);
  const [standorte, setStandorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchFt, setSearchFt] = useState('');

  // Form state
  const [formName, setFormName] = useState('');
  const [formDatum, setFormDatum] = useState('');
  const [formJaehrlich, setFormJaehrlich] = useState(true);
  const [formStandortId, setFormStandortId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [ft, st] = await Promise.all([
      api(`/api/feiertage?jahr=${jahr}`),
      api('/api/standorte'),
    ]);
    setFeiertage(ft);
    setStandorte(st);
    setLoading(false);
  }, [jahr]);

  useEffect(() => { load(); }, [load]);

  // Safely extract YYYY-MM-DD from date string or Date object (timezone-safe)
  function safeDate(d) {
    if (!d) return '';
    if (typeof d === 'string') return d.split('T')[0];
    // Date object: use local parts to avoid UTC shift
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // Build feiertagSet: Map<dateString, { name, id, ... }>
  const feiertagMap = useMemo(() => {
    const m = new Map();
    for (const f of feiertage) {
      const ds = safeDate(f.datum);
      m.set(ds, f);
    }
    return m;
  }, [feiertage]);

  // Current month stats
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const stats = useMemo(() => {
    // Arbeitstage im aktuellen Monat
    const m = now.getMonth();
    const y = jahr;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    let arbeitstage = 0;
    let spitzentage = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(y, m, d);
      const jsDay = date.getDay();
      const isWeekend = jsDay === 0 || jsDay === 6;
      const ds = isoDate(date);
      const isFeiertag = feiertagMap.has(ds);
      if (!isWeekend && !isFeiertag) {
        arbeitstage++;
        // Spitzentag = previous day was weekend or feiertag
        if (d > 1) {
          const prev = new Date(y, m, d - 1);
          const prevDay = prev.getDay();
          const prevWe = prevDay === 0 || prevDay === 6;
          const prevFt = feiertagMap.has(isoDate(prev));
          if (prevWe || prevFt) spitzentage++;
        } else {
          // First of month: check last day of prev month
          const prev = new Date(y, m, 0);
          const prevDay = prev.getDay();
          const prevWe = prevDay === 0 || prevDay === 6;
          const prevFt = feiertagMap.has(isoDate(prev));
          if (prevWe || prevFt) spitzentage++;
        }
      }
    }

    // Next holiday
    const todayStr = isoDate(now);
    const upcoming = feiertage
      .map(f => ({ ...f, ds: safeDate(f.datum) }))
      .filter(f => f.ds >= todayStr)
      .sort((a, b) => a.ds.localeCompare(b.ds));
    const nextHoliday = upcoming[0] || null;

    // Total arbeitstage im Jahr
    let jahresArbeitstage = 0;
    for (let mo = 0; mo < 12; mo++) {
      const dim = new Date(y, mo + 1, 0).getDate();
      for (let d = 1; d <= dim; d++) {
        const date = new Date(y, mo, d);
        const jsDay = date.getDay();
        const isWeekend = jsDay === 0 || jsDay === 6;
        const ds = isoDate(date);
        if (!isWeekend && !feiertagMap.has(ds)) jahresArbeitstage++;
      }
    }

    return { arbeitstage, spitzentage, nextHoliday, jahresArbeitstage };
  }, [feiertage, feiertagMap, jahr]);

  const filteredFeiertage = useMemo(() => {
    if (!searchFt) return feiertage;
    const q = searchFt.toLowerCase();
    return feiertage.filter(f => f.name?.toLowerCase().includes(q));
  }, [feiertage, searchFt]);

  // ─── CRUD ──────────────────────────────────────────────────────

  function openNew() {
    setEditItem(null);
    setFormName('');
    setFormDatum('');
    setFormJaehrlich(true);
    setFormStandortId('');
    setModalOpen(true);
  }

  function openEdit(f) {
    setEditItem(f);
    setFormName(f.name);
    setFormDatum(safeDate(f.datum));
    setFormJaehrlich(f.jaehrlich);
    setFormStandortId(f.standort_id || '');
    setModalOpen(true);
  }

  async function handleSave() {
    const body = {
      name: formName,
      datum: formDatum,
      jaehrlich: formJaehrlich,
      standort_id: formStandortId || null,
    };
    if (editItem) {
      await api(`/api/feiertage/${editItem.id}`, { method: 'PUT', body });
    } else {
      await api('/api/feiertage', { method: 'POST', body });
    }
    setModalOpen(false);
    load();
  }

  async function handleDelete(id) {
    await api(`/api/feiertage/${id}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    load();
  }

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="min-h-screen p-page">
      <PageHeader
        title={t('holidays.title')}
        subtitle={t('holidays.subtitle')}
        className="mb-6"
        actions={
          <button
            onClick={openNew}
            className="px-4 py-2 bg-amber-500/15 text-amber-400 rounded-lg text-sm font-semibold hover:bg-amber-500/25 transition-colors border border-amber-500/20"
          >
            + {t('holidays.newHoliday')}
          </button>
        }
      />

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('holidays.workDays')} {MONATE[now.getMonth()]}</div>
          <div className="text-2xl font-extrabold text-content font-mono">{stats.arbeitstage}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('holidays.nextHoliday')}</div>
          {stats.nextHoliday ? (
            <div>
              <div className="text-sm font-bold text-amber-400">{stats.nextHoliday.name}</div>
              <div className="text-[11px] text-[var(--text-secondary)] font-mono">
                {(() => {
                  const ds = stats.nextHoliday.ds;
                  const d = new Date(ds + 'T12:00:00');
                  return `${d.getDate()}. ${MONATE[d.getMonth()]}`;
                })()}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-tertiary)]">{t('holidays.noMore')}</div>
          )}
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('holidays.peakDays')} {MONATE[now.getMonth()]}</div>
          <div className="text-2xl font-extrabold text-content font-mono">{stats.spitzentage}</div>
          <div className="text-[10px] text-[var(--text-tertiary)]">{t('holidays.daysAfterHolidayWeekend')}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('holidays.workDaysYear')} {jahr}</div>
          <div className="text-2xl font-extrabold text-content font-mono">{stats.jahresArbeitstage}</div>
          <div className="text-[10px] text-[var(--text-tertiary)]">{feiertage.length} {t('holidays.holidaysCount')}</div>
        </div>
      </div>

      {/* Year Navigation */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <button
          onClick={() => setJahr(j => j - 1)}
          className="px-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-active transition-colors font-mono"
        >
          &larr; {jahr - 1}
        </button>
        <span className="text-xl font-extrabold text-content font-mono px-4">{jahr}</span>
        <button
          onClick={() => setJahr(j => j + 1)}
          className="px-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-active transition-colors font-mono"
        >
          {jahr + 1} &rarr;
        </button>
      </div>

      <div className="flex flex-col xl:flex-row gap-6">
        {/* Calendar Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="text-center py-20 text-[var(--text-tertiary)]">{t('holidays.loadingCalendar')}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 12 }, (_, i) => (
                <MonthGrid
                  key={i}
                  jahr={jahr}
                  monat={i}
                  feiertagSet={feiertagMap}
                  onDayClick={(f) => openEdit(f)}
                  t={t}
                  MONATE={MONATE}
                  WOCHENTAGE_SHORT={WOCHENTAGE_SHORT}
                />
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: Feiertage-Liste */}
        <div className="xl:w-[420px] flex-shrink-0">
          <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-content">{t('holidays.holidaysYear')} {jahr}</h2>
                <span className="text-xs text-[var(--text-secondary)] font-mono">{searchFt ? `${filteredFeiertage.length} / ` : ''}{feiertage.length}</span>
              </div>
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                <input type="text" value={searchFt} onChange={e => setSearchFt(e.target.value)} placeholder={`${t('common.search')}...`} className="w-full pl-8 pr-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content placeholder-content-dim focus:outline-none focus:border-amber-500/50" />
              </div>
            </div>

            {filteredFeiertage.length === 0 ? (
              <div className="p-8 text-center text-[var(--text-tertiary)] text-sm">
                {searchFt ? t('holidays.noResults', { query: searchFt }) : t('holidays.noHolidays', { year: jahr })}
                {!searchFt && <><br /><button onClick={openNew} className="mt-2 text-amber-400 hover:underline">+ {t('holidays.addHoliday')}</button></>}
              </div>
            ) : (
              <div className="divide-y divide-line max-h-[calc(100vh-320px)] overflow-y-auto">
                {filteredFeiertage.map((f) => {
                  const ds = safeDate(f.datum);
                  const d = new Date(ds + 'T12:00:00');
                  const dayName = DAY_NAMES[d.getDay()];
                  const standortName = f.standort_id ? standorte.find(s => s.id === f.standort_id)?.name : t('common.all');

                  return (
                    <div key={f.id} className="px-4 py-3 flex items-center gap-3 hover:bg-surface-card group">
                      {/* Date badge */}
                      <div className="w-11 h-11 rounded-lg bg-red-500/10 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-red-400 uppercase leading-none">{MONATE_SHORT[d.getMonth()]}</span>
                        <span className="text-sm font-extrabold text-red-300 leading-tight">{d.getDate()}</span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-content truncate">{f.name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-[var(--text-secondary)] font-mono">{dayName}, {d.getDate()}.{d.getMonth()+1}.{d.getFullYear()}</span>
                          {f.jaehrlich && (
                            <span className="text-[9px] bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded font-medium">{t('holidays.yearly')}</span>
                          )}
                          <span className="text-[10px] text-[var(--text-tertiary)]">{standortName}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEdit(f)}
                          className="p-1.5 rounded-lg hover:bg-surface-active text-[var(--text-secondary)] hover:text-content transition-colors"
                          title={t('common.edit')}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(f)}
                          className="p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--text-secondary)] hover:text-red-400 transition-colors"
                          title={t('common.delete')}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-3 bg-surface-card border border-line rounded-card p-4 shadow-card">
            <h3 className="text-xs font-bold text-content-tertiary uppercase tracking-wider mb-3">{t('holidays.legend')}</h3>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500/20 border border-red-500/30" />
                <span className="text-[11px] text-content-tertiary">{t('holidays.holiday')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-surface-card border border-line" />
                <span className="text-[11px] text-content-tertiary">{t('holidays.weekend')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded ring-1 ring-amber-500/50" />
                <span className="text-[11px] text-content-tertiary">{t('holidays.today')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[var(--text-secondary)] font-mono">AT</span>
                <span className="text-[11px] text-content-tertiary">= {t('holidays.workDaysAbbrev')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── New/Edit Modal ───────────────────────────────────────── */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? t('holidays.editHoliday') : t('holidays.newHoliday')}
      >
        <Field label={t('holidays.nameLabel')}>
          <Input
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={t('holidays.namePlaceholder')}
          />
        </Field>

        <Field label={t('holidays.dateLabel')}>
          <Input
            type="date"
            value={formDatum}
            onChange={(e) => setFormDatum(e.target.value)}
          />
        </Field>

        <Field label={t('holidays.recurringYearly')}>
          <ToggleSwitch checked={formJaehrlich} onChange={setFormJaehrlich} />
        </Field>

        <Field label={t('holidays.locationLabel')}>
          <Select value={formStandortId} onChange={(e) => setFormStandortId(e.target.value)}>
            <option value="">{t('holidays.allLocations')}</option>
            {standorte.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => setModalOpen(false)}
            className="flex-1 px-4 py-2.5 bg-surface-inset border border-line-hover rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-active transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!formName || !formDatum}
            className="flex-1 px-4 py-2.5 bg-amber-500/15 text-amber-400 border border-amber-500/20 rounded-lg text-sm font-semibold hover:bg-amber-500/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {editItem ? t('common.save') : t('holidays.createButton')}
          </button>
        </div>
      </Modal>

      {/* ─── Delete Confirm ───────────────────────────────────────── */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t('holidays.deleteHoliday')}
      >
        <p className="text-sm text-content-secondary mb-6">
          {t('holidays.confirmDelete', { name: deleteConfirm?.name })}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setDeleteConfirm(null)}
            className="flex-1 px-4 py-2.5 bg-surface-inset border border-line-hover rounded-lg text-sm text-content-tertiary hover:text-content transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => handleDelete(deleteConfirm.id)}
            className="flex-1 px-4 py-2.5 bg-red-500/15 text-red-400 border border-red-500/20 rounded-lg text-sm font-semibold hover:bg-red-500/25 transition-colors"
          >
            {t('common.delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
